/**
 * Feedback Buttons Component
 *
 * Thumbs up/down buttons for message feedback.
 */

import { LitElement, html, css } from 'lit';
import { baseStyles } from '../styles/shared-styles.js';

/**
 * FeedbackButtons - Thumbs up/down feedback
 * @element feedback-buttons
 * @fires feedback - When a feedback button is clicked
 */
export class FeedbackButtons extends LitElement {
    static properties = {
        messageId: { type: String, attribute: 'message-id' },
        selected: { type: String, state: true } // 'up', 'down', or null
    };

    static styles = [
        baseStyles,
        css`
            :host {
                display: inline-flex;
            }

            .feedback {
                display: flex;
                align-items: center;
                gap: 0;
            }

            .feedback__btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                padding: 0;
                background: transparent;
                border: 1px solid transparent;
                border-radius: 50%;
                cursor: pointer;
                color: var(--text-secondary);
                transition: all var(--transition-fast);
            }

            .feedback__btn:hover:not(:disabled) {
                background-color: var(--bg-light);
                border-color: var(--border-color);
            }

            .feedback__btn:disabled {
                cursor: default;
            }

            .feedback__btn svg {
                width: 14px;
                height: 14px;
            }

            .feedback__btn--up.selected {
                color: var(--success-color);
                background-color: rgba(40, 167, 69, 0.1);
                border-color: var(--success-color);
            }

            .feedback__btn--down.selected {
                color: var(--danger-color);
                background-color: rgba(220, 53, 69, 0.1);
                border-color: var(--danger-color);
            }

            .feedback__label {
                font-size: 11px;
                color: var(--text-secondary);
                margin-right: 4px;
            }
        `
    ];

    constructor() {
        super();
        this.messageId = '';
        this.selected = null;
    }

    render() {
        const isDisabled = this.selected !== null;

        return html`
            <div class="feedback">
                <span class="feedback__label">Helpful?</span>

                <button
                    class="feedback__btn feedback__btn--up ${this.selected === 'up' ? 'selected' : ''}"
                    ?disabled=${isDisabled}
                    @click=${() => this._handleFeedback('up')}
                    aria-label="Helpful"
                    title="This was helpful">
                    ${this._renderThumbsUp()}
                </button>

                <button
                    class="feedback__btn feedback__btn--down ${this.selected === 'down' ? 'selected' : ''}"
                    ?disabled=${isDisabled}
                    @click=${() => this._handleFeedback('down')}
                    aria-label="Not helpful"
                    title="This was not helpful">
                    ${this._renderThumbsDown()}
                </button>
            </div>
        `;
    }

    _renderThumbsUp() {
        return html`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
            </svg>
        `;
    }

    _renderThumbsDown() {
        return html`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
            </svg>
        `;
    }

    /**
     * Handle feedback button click
     */
    _handleFeedback(feedback) {
        if (this.selected) return; // Already selected

        this.selected = feedback;

        this.dispatchEvent(new CustomEvent('feedback', {
            detail: {
                messageId: this.messageId,
                feedback
            },
            bubbles: true,
            composed: true
        }));
    }
}

customElements.define('feedback-buttons', FeedbackButtons);
